import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import mpegts from "mpegts.js";
import { AlertTriangle, Loader2 } from "lucide-react";

const OFFLINE = "This stream could not be loaded. It may be offline or blocked by your provider.";

function attach(v, cur, live, fail, onReady) {
  if (cur.type === "hls" && Hls.isSupported()) {
    const hls = new Hls({ maxBufferLength: 30, enableWorker: true });
    hls.loadSource(cur.url);
    hls.attachMedia(v);
    hls.on(Hls.Events.MANIFEST_PARSED, onReady);
    hls.on(Hls.Events.ERROR, (_, d) => {
      if (!d.fatal) return;
      if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
      else fail(OFFLINE);
    });
    return () => hls.destroy();
  }
  if (cur.type === "ts" && mpegts.isSupported()) {
    const p = mpegts.createPlayer({ type: "mpegts", isLive: live, url: cur.url }, { enableWorker: false, liveBufferLatencyChasing: live });
    p.attachMediaElement(v);
    p.on(mpegts.Events.ERROR, () => fail(OFFLINE));
    p.load();
    onReady();
    return () => {
      p.pause();
      p.unload();
      p.detachMediaElement();
      p.destroy();
    };
  }
  v.src = cur.url;
  onReady();
  return null;
}

export default function Player({ sources, live = false, startAt = 0, onProgress, onSourceOk, onEnded }) {
  const ref = useRef(null);
  const idxRef = useRef(0);
  const lastSave = useRef(0);
  const okSent = useRef(false);
  const [idx, setIdx] = useState(0);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = sources.map((s) => s.url).join("|");
  const cur = sources[idx] || sources[0];

  useEffect(() => {
    idxRef.current = 0;
    setIdx(0);
  }, [key]);

  useEffect(() => {
    const v = ref.current;
    if (!v || !cur) return;
    setErr(null);
    setLoading(true);
    okSent.current = false;
    let dead = false;
    const fail = (msg) => {
      if (dead) return;
      dead = true;
      if (idxRef.current + 1 < sources.length) {
        idxRef.current += 1;
        setIdx(idxRef.current);
      } else {
        setLoading(false);
        setErr(msg);
      }
    };
    v.onerror = () => fail("This video format can't be played here. Try another title or stream.");
    const onReady = () => {
      if (startAt > 0 && !live) {
        const seek = () => (v.currentTime = startAt);
        v.readyState >= 1 ? seek() : v.addEventListener("loadedmetadata", seek, { once: true });
      }
      v.play().catch(() => {});
    };
    const cleanup = attach(v, cur, live, fail, onReady);
    return () => {
      dead = true;
      v.onerror = null;
      cleanup?.();
      v.removeAttribute("src");
      v.load();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur?.url]);

  const save = (force) => {
    const v = ref.current;
    if (!onProgress || live || !v || !v.duration || !isFinite(v.duration)) return;
    if (!force && Date.now() - lastSave.current < 5000) return;
    lastSave.current = Date.now();
    onProgress(v.currentTime, v.duration);
  };

  return (
    <div className="relative w-full h-full bg-black" data-testid="video-player" data-source-type={cur?.type}>
      <video
        ref={ref}
        controls
        playsInline
        className="w-full h-full object-contain bg-black"
        onPlaying={() => {
          setLoading(false);
          if (!okSent.current) {
            okSent.current = true;
            onSourceOk?.(cur.type);
          }
        }}
        onWaiting={() => setLoading(true)}
        onTimeUpdate={() => save(false)}
        onPause={() => save(true)}
        onEnded={() => {
          save(true);
          onEnded?.();
        }}
        data-testid="video-element"
      />
      {loading && !err && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <Loader2 className="w-10 h-10 text-[#00E5FF] animate-spin" />
          {idx > 0 && <span className="absolute mt-20 text-xs font-mono text-slate-400" data-testid="player-fallback-note">Trying backup format…</span>}
        </div>
      )}
      {err && (
        <div className="absolute inset-0 grid place-items-center bg-black/80 p-6" data-testid="player-error">
          <div className="text-center max-w-sm">
            <AlertTriangle className="w-8 h-8 mx-auto text-[#FFB800] mb-3" />
            <p className="text-sm text-slate-300">{err}</p>
          </div>
        </div>
      )}
    </div>
  );
}
